import { useState, useEffect, useRef, useContext } from 'react'
import { AdminContext } from 'context/AdminContext'
import { WhichWeightAPI } from 'WhichWeightAPI'
import { MagnifyingGlassIcon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/20/solid'

export default function UserDirectoryOrganizationDropdown() {
    const { UserDirectorySubContext } = useContext(AdminContext)

    const [organizations, setOrganizations] = useState([])
    const [organizationsLoading, setOrganizationsLoading] = useState(true)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const dropdownRef = useRef(null)

    const FetchOrganizations = async () => {
        try {
            setOrganizationsLoading(true)
            const result = await WhichWeightAPI('REPORTING', 'GET', '/organization_directory')
            
            if (result.data.status === 'failure') {
                setOrganizations([])
                return
            }

            if (result.data.data) {
                // console.log(result.data.data)
                setOrganizations(result.data.data)
            }
        } catch (error) {
            console.error('Error fetching organizations:', error)
        } finally {
            setOrganizationsLoading(false)
        }
    }

    const HandleClickOutsideDropdown = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setIsDropdownOpen(false)
            setSearchTerm('')
        }
    }

    const GetSelectedOrganization = () => {
        if (!UserDirectorySubContext.userDirectoryFilters.organizationFilter) return { name: 'All Organizations', logo: null }
        if (UserDirectorySubContext.userDirectoryFilters.organizationFilter === 'individual') return { name: 'Individual users', logo: null }
        const org = organizations.find(o => o.id.toString() === UserDirectorySubContext.userDirectoryFilters.organizationFilter)
        return org || { name: 'All Organizations', logo: null }
    }

    useEffect(() => { FetchOrganizations() }, [])

    useEffect(() => {
        document.addEventListener('mousedown', HandleClickOutsideDropdown)
        return () => {
            document.removeEventListener('mousedown', HandleClickOutsideDropdown)
        }
    }, [])

    const filteredOrganizations = organizations.filter(org => org.name.toLowerCase().includes(searchTerm.toLowerCase()))
    const selectedOrg = GetSelectedOrganization()
    const hasSelection = Boolean(UserDirectorySubContext.userDirectoryFilters.organizationFilter)

    return (
        <div className="relative" ref={dropdownRef}>
            <div className="w-full flex items-center rounded-md border border-gray-300 bg-white shadow-sm focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 hover:border-gray-400">
                <button
                    type="button"
                    className="flex flex-1 items-center gap-x-3 min-w-0 px-3 py-2 text-left focus:outline-none"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                    {selectedOrg.logo ? (
                        <img
                            src={selectedOrg.logo}
                            alt={`${selectedOrg.name} logo`}
                            className="w-5 h-5 rounded-full border border-gray-200 flex-shrink-0"
                            onError={(e) => {
                                e.target.style.display = 'none'
                            }}
                        />
                    ) : (
                        <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs text-gray-500">A</span>
                        </div>
                    )}

                    <span className="truncate">{selectedOrg.name}</span>
                </button>

                {hasSelection && (
                    <button
                        type="button"
                        className="p-1 rounded-full hover:bg-gray-100 transition-colors focus:outline-none"
                        onClick={() => {
                            UserDirectorySubContext.HandleUserDirectoryFiltersChange('organizationFilter', '')
                        }}
                        title="Clear selection"
                    >
                        <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    </button>
                )}

                <button
                    type="button"
                    className="px-2 py-2 focus:outline-none"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    title="Toggle organization list"
                >
                    <ChevronDownIcon
                        className={`
                            h-5 w-5 text-gray-400 transition-transform
                            ${isDropdownOpen ? 'rotate-180' : ''}
                        `}
                    />
                </button>
            </div>

            {isDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-300 bg-white shadow-lg">
                    <div className="max-h-[400px] overflow-auto">
                        {/* Search Input */}
                        <div className="sticky top-0 bg-white border-b border-gray-200 p-2">
                            <div className="relative">
                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                    <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    placeholder="Search organizations..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    autoFocus
                                />
                            </div>
                        </div>

                        <div
                            className="flex items-center gap-x-3 px-3 py-2 cursor-pointer hover:bg-gray-100"
                            onClick={() => {
                                UserDirectorySubContext.HandleUserDirectoryFiltersChange('organizationFilter', '');
                                setIsDropdownOpen(false);
                                setSearchTerm('');
                            }}
                        >
                            <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center">
                                <span className="text-xs text-gray-500">A</span>
                            </div>

                            <span>All Organizations</span>
                        </div>

                        <div
                            className="flex items-center gap-x-3 px-3 py-2 cursor-pointer hover:bg-gray-100"
                            onClick={() => {
                                UserDirectorySubContext.HandleUserDirectoryFiltersChange('organizationFilter', 'individual');
                                setIsDropdownOpen(false);
                                setSearchTerm('');
                            }}
                        >
                            <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center">
                                <span className="text-xs text-gray-500">I</span>
                            </div>

                            <span>Individual users</span>
                        </div>

                        {organizationsLoading && (
                            <div className="px-3 py-2 text-sm text-gray-500">
                                Loading organizations...
                            </div>
                        )}

                        {!organizationsLoading && filteredOrganizations.length === 0 && searchTerm && (
                            <div className="px-3 py-2 text-sm text-gray-500">
                                No organizations found
                            </div>
                        )}

                        {!organizationsLoading && filteredOrganizations.map((org) => (
                            <div
                                key={org.id}
                                className="flex items-center gap-x-3 px-3 py-2 cursor-pointer hover:bg-gray-100"
                                onClick={() => {
                                    UserDirectorySubContext.HandleUserDirectoryFiltersChange('organizationFilter', org.id.toString());
                                    setIsDropdownOpen(false);
                                    setSearchTerm('');
                                }}
                            >
                                {org.logo ? (
                                    <img
                                        src={org.logo}
                                        alt={`${org.name} logo`}
                                        className="w-5 h-5 rounded-full border border-gray-200"
                                        onError={(e) => {
                                            e.target.style.display = 'none'
                                        }}
                                    />
                                ) : (
                                    <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center">
                                        <span className="text-xs text-gray-500">A</span>
                                    </div>
                                )}

                                <span className="truncate">{org.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}